"use client";

import type { AccountStatusResponse } from "@/helpers/api/WRA/registration/getAccountStatus";
import { v4 } from "uuid";

type QueryValue = boolean | number | string | undefined;
type QueryParams = Record<string, QueryValue | QueryValue[]>;

type UserLocation = {
  countryName: string;
  isoCountryCode: string;
};

const parseResponse = async <ResponseType>(response: Response) => {
  if (!response.ok) {
    throw new Error(
      `Builder API request failed with status ${response.status}`,
    );
  }

  if (response.status === 204) return undefined as ResponseType;

  return response.json() as Promise<ResponseType>;
};

const get = async <ResponseType>(path: string, params: QueryParams = {}) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([name, value]) => {
    const values = Array.isArray(value) ? value : [value];
    values.forEach((item) => {
      if (item !== undefined) searchParams.append(name, String(item));
    });
  });

  const query = searchParams.size ? `?${searchParams}` : "";
  const response = await fetch(`/api/builder/${path}${query}`);

  return parseResponse<ResponseType>(response);
};

const post = async <ResponseType>(path: string, body: unknown) => {
  const response = await fetch(`/api/builder/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  return parseResponse<ResponseType>(response);
};

export const validateEmail = (
  email: string,
  requestId: string | null,
  correlationId?: string,
) =>
  get<unknown>("validation/email", {
    email,
    requestId: requestId ?? undefined,
    correlationId,
  });

export const validateUniqueEmail = (
  email: string,
  type: string,
  correlationId?: string,
  dataCenterType?: 0 | 1,
) =>
  get<unknown>("validation/unique-email", {
    email,
    type,
    correlationId,
    dataCenterType,
  });

export const validateFreeEmailDomain = (email: string) =>
  get<unknown>("validation/free-email", { email });

export const validatePostalCode = (payload: {
  twoAlphaCountryCode: string;
  postalCode: string;
  correlationId?: string;
}) =>
  get<unknown>("validation/postal-code", {
    country: payload.twoAlphaCountryCode,
    postalCode: payload.postalCode,
    correlationId: payload.correlationId,
  });

export const validateUniqueCompany = (
  companyName: string,
  type: string,
  correlationId?: string,
) =>
  get<unknown>("validation/unique-company", {
    companyName,
    type,
    correlationId,
  });

export const validateUniqueNickname = (
  organization: string,
  type: string,
  dataCenterType: 0 | 1,
  correlationId?: string,
) =>
  get<unknown>("validation/unique-nickname", {
    organization,
    type,
    dataCenterType,
    correlationId,
  });

export const validatePhone = (
  phone: string,
  country: string,
  requestId: string | null,
  correlationId?: string,
) =>
  get<unknown>("validation/phone", {
    phone,
    country,
    requestId: requestId ?? undefined,
    correlationId,
  });

export const submitRegistration = (body: unknown) =>
  post<unknown>("registration", body);
export const submitQuickQuoteRegistration = (body: unknown) =>
  post<unknown>("registration/quick-quote", body);
export const submitQuoteRegistration = (body: unknown) =>
  post<unknown>("registration/quote", body);
export const submitSolarwindsCustomerProfessional = (body: unknown) =>
  post<unknown>("registration/scp", body);
export const submitTrustCenterRegistration = (body: unknown) =>
  post<unknown>("registration/trust-center", body);
export const reportAccessibilityProblem = (body: unknown) =>
  post<void>("registration/accessibility-concern", body);

export const getCountries = () => get<unknown>("countries");
export const getUserLocation = () =>
  get<UserLocation | undefined>("ip-geo/user-location");
export const getAccountStatus = () =>
  get<AccountStatusResponse>("registration/account-status");
export const getRegistrationDetails = () =>
  get<string | undefined>("registration/prefill");

export const encryptString = (value: unknown) =>
  post<unknown>("encryption/encrypt", { value });
export const decryptString = (value: string) =>
  post<Record<string, unknown>>("encryption/decrypt", { value });

export const validateRequest = (pageUrl: string, requestId?: string) =>
  post<unknown>("validation/request", { pageUrl, requestId });

export const customBuilderContextFunctions = {
  generateUuid: v4,
  validateEmail,
  validateUniqueEmail,
  validateFreeEmailDomain,
  validatePostalCode,
  validateUniqueCompany,
  validateUniqueNickname,
  validatePhone,
  submitRegistration,
  submitQuickQuoteRegistration,
  submitQuoteRegistration,
  submitSolarwindsCustomerProfessional,
  submitTrustCenterRegistration,
  reportAccessibilityProblem,
  encryptString,
  getCountries,
  getUserLocation,
};

export type CustomBuilderContextFunctionNamesType =
  keyof typeof customBuilderContextFunctions;
